import useOverlay from "@/hooks/common/useOverlay";
import {useTaggingClick} from "@/hooks/common/useTagging";
import {getDisplayCouponsByEvent} from "@COMPONENTS/common/couponUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import Alert from "@COMPONENTS/common/modal/alert/Mobile";
import SSOLink from "@COMPONENTS/common/ssoLink";
import {TDetailPackage} from "@COMPONENTS/product/types";
import * as style from "@COMPONENTS/style/detailpackage/itineraryM";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {TextDefaultButton} from "@FRAMEWORK/ui/mobile/button";
import {Area, Flex} from "@FRAMEWORK/ui/mobile/layout";
import BulletListItem from "@FRAMEWORK/ui/mobile/list/BulletListItem";
import {SubTitle} from "@FRAMEWORK/ui/mobile/text";
import useCustomerMe from "@QUERIES/account/useCustomerMe";
import useCouponByEvent, {CouponByEvent} from "@QUERIES/prdt/coupon/useCouponByEvent";
import couponDownAsync, {RequestBody} from "@QUERIES/prdt/coupon/useCouponDown";
import useEventPrice from "@QUERIES/prdt/event/useEventPrice";
import {useQueryClient} from "@tanstack/react-query";
import {format} from "@yb/utils";
import dayjs from "dayjs";
import {memo, useEffect, useRef, useState} from "react";

/** 선착순 쿠폰 마감임박 잔여 수량 기준 */
const FCFS_IMMINENT_REMAIN_CNT = 20;

const DetailCouponComponent = (props: TDetailPackage) => {
	const {query} = props;
	const {data: loginInfo, isLoading: isLoginLoading} = useCustomerMe();
	const webCustNo = loginInfo?.body?.nonUser === false ? loginInfo.body.webCustNo : undefined;
	const CouponByEventFetch = useCouponByEvent(query.evCd, webCustNo, {enabled: !isLoginLoading});
	const coupon = CouponByEventFetch.data?.body;

	const eventPriceFetch = useEventPrice(query.evCd);
	const eventPrice = eventPriceFetch.data?.body;

	const displayCoupons = getDisplayCouponsByEvent(coupon, eventPrice?.adtPrice);

	const [open] = useOverlay();
	const queryClient = useQueryClient();
	const [tooltipIndex, setTooltipIndex] = useState<number | null>(null);
	const tooltipRef = useRef<HTMLElement>(null);

	useEffect(() => {
		if (tooltipIndex === null) return;
		const handleClickOutside = (e: MouseEvent) => {
			if (tooltipRef.current && e.target instanceof Node && !tooltipRef.current.contains(e.target)) {
				setTooltipIndex(null);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [tooltipIndex]);

	const toggleTooltip = (index: number) => setTooltipIndex(tooltipIndex === index ? null : index);

	const {taggingClick} = useTaggingClick("goods_down_coupon", {evCd: query.evCd, selectDiv: "MiddtlButton"});

	const handleCoupon = async (coupon: CouponByEvent) => {
		const {couponNo, fcfsYn, remainCnt} = coupon;
		if (fcfsYn === "Y" && Number(remainCnt) <= 0) return;

		if (loginInfo?.body.nonUser === false) {
			try {
				taggingClick(coupon);
				const body: RequestBody = {
					compId: "1",
					webCustNo: loginInfo.body.webCustNo,
					couponNo,
				};
				const result = await couponDownAsync(body);
				const {data} = result;
				if (data.errorCode === "0000") {
					open(prop => <Alert {...prop} text={data.errorMsg} />);
					queryClient.refetchQueries({queryKey: ["useCouponByEvent"], type: "active"});
				} else if (data.errorCode === "2004") {
					open(prop => <Alert {...prop} text="이미 쿠폰을 발급받으셨습니다." />);
				} else {
					alert(data.errorMsg);
				}
			} catch (error) {
				alert("오류가 발생하였습니다.");
			}
		} else {
			open(prop => (
				<Alert
					{...prop}
					alertType="confirm"
					text="로그인 후 이용하실 수 있습니다. <br/>로그인하시겠습니까?"
					afterConfirm={() => LoginUtil.login()}
				/>
			));
		}
	};

	return (
		<>
			{displayCoupons.length > 0 && (
				<Area>
					<style.TitleWrap>
						<SubTitle>사용 가능 쿠폰</SubTitle>
						<style.CouponTotalText>
							총 <em>{displayCoupons.length}</em>건
						</style.CouponTotalText>
					</style.TitleWrap>
					<style.CouponList>
						{displayCoupons.map((item, index) => {
							const isFcfs = item.fcfsYn === "Y";
							const remainCnt = Number(item.remainCnt);
							const isSoldOut = isFcfs && remainCnt <= 0;
							const isImpending = isFcfs && !isSoldOut && remainCnt <= FCFS_IMMINENT_REMAIN_CNT;

							let couponButton = (
								<TextDefaultButton
									size="default"
									types="subCtn"
									text="쿠폰받기"
									onClick={() => handleCoupon(item)}
								/>
							);
							if (isSoldOut) {
								couponButton = <TextDefaultButton size="default" types="subCtn" text="마감" disabled />;
							} else if (item.downYn === "N") {
								couponButton = <TextDefaultButton size="default" types="whiteCtn" text="사용가능" />;
							}

							return (
								<style.CouponItem key={index}>
									<Flex gap="10" align="center" justify="space-between">
										<style.CouponContent>
											<style.CouponIcon>
												{item.discDiviCd === "M" ? (
													<i>{format.koKRFormatNumber(item.discAmt)}원</i>
												) : (
													<i>{item.discRate}%</i>
												)}
												{isFcfs && (
													<>
														<style.CouponTagGroup>
															<style.CouponTag type="limited">선착순사용</style.CouponTag>
															{isImpending && <style.CouponTag type="impending">마감임박</style.CouponTag>}
														</style.CouponTagGroup>
														<style.TooltipWrap ref={tooltipIndex === index ? tooltipRef : undefined}>
															<style.TooltipButton type="button" onClick={() => toggleTooltip(index)} />
															{tooltipIndex === index && (
																<style.Tooltip>
																	선착순 사용 가능 수량이 마감될 경우, 쿠폰을 다운로드 하더라도 할인 쿠폰 적용이
																	불가합니다.
																</style.Tooltip>
															)}
														</style.TooltipWrap>
													</>
												)}
											</style.CouponIcon>
											<style.CouponNameWrap>
												<style.CouponTitle>{item.couponNm}</style.CouponTitle>
											</style.CouponNameWrap>
											<style.CouponExpired>
												{item.validityDiviCd === "DD"
													? `발급일로부터 ${item.validityDday}일 유효`
													: `${dayjs(item.validityFrDt).format("YYYY.MM.DD")} ~ 
										${dayjs(item.validityToDt).format("YYYY.MM.DD")}`}
											</style.CouponExpired>
										</style.CouponContent>
										{couponButton}
									</Flex>
								</style.CouponItem>
							);
						})}
					</style.CouponList>
					<style.BulletBox>
						<ul>
							<BulletListItem>
								해당 쿠폰은 행사 전용 혜택으로, 회원 등급별 쿠폰을 다운받으시려면&nbsp;
								<SSOLink
									href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/memberGrade.yb`}
									rel="noreferrer"
									target="_blank"
								>
									이곳
								</SSOLink>
								을 클릭해주세요.
							</BulletListItem>
						</ul>
					</style.BulletBox>
				</Area>
			)}
		</>
	);
};

const DetailCoupon = (props: TDetailPackage) => (
	<ErrorBoundary logMsg="EventDetailCoupon Error">
		<DetailCouponComponent {...props} />
	</ErrorBoundary>
);

export default memo(DetailCoupon);
